/**
 * Contrôler une écriture avant de l'envoyer à la base.
 *
 * La base est seule juge : `valider_ecriture()` refuse d'elle-même une
 * écriture déséquilibrée, à moins de deux lignes, ou dont une ligne porte à la
 * fois un débit et un crédit. Ce module rend le même verdict **avant** l'aller
 * et retour, pour que l'écran de saisie d'une OD puisse dire où est l'écart
 * pendant qu'on tape.
 *
 * Les étiquettes d'erreur sont celles de la base (`[desequilibre]`,
 * `[lignes]`...) : un écran n'a qu'une table de libellés à tenir, quelle que
 * soit la couche qui a refusé.
 */

export interface LigneSaisie {
  compte: string;
  debitCents: number;
  creditCents: number;
}

export type EtiquetteDeRefus = '[lignes]' | '[desequilibre]' | '[ligne-sens]' | '[montant]';

export interface Refus {
  etiquette: EtiquetteDeRefus;
  /** Indice de la ligne en cause, quand le refus en vise une. */
  ligne?: number;
  message: string;
}

export interface Controle {
  /** Rien ne s'oppose à la validation, du point de vue des montants. */
  validable: boolean;
  totalDebitCents: number;
  totalCreditCents: number;
  /** Débit moins crédit. Positif : il manque du crédit. */
  ecartCents: number;
  refus: Refus[];
}

export function controlerEcriture(lignes: readonly LigneSaisie[]): Controle {
  const refus: Refus[] = [];
  let debit = 0;
  let credit = 0;

  lignes.forEach((l, i) => {
    const montantsValides =
      Number.isSafeInteger(l.debitCents) && l.debitCents >= 0 &&
      Number.isSafeInteger(l.creditCents) && l.creditCents >= 0;
    if (!montantsValides) {
      refus.push({
        etiquette: '[montant]',
        ligne: i,
        message: 'un montant est un entier de centimes positif ou nul',
      });
      return;
    }
    // Un débit OU un crédit, strictement positif : ni les deux, ni aucun.
    if ((l.debitCents > 0) === (l.creditCents > 0)) {
      refus.push({
        etiquette: '[ligne-sens]',
        ligne: i,
        message: l.debitCents > 0
          ? 'une ligne porte un débit ou un crédit, pas les deux'
          : 'une ligne sans montant n\'a rien à dire',
      });
    }
    debit += l.debitCents;
    credit += l.creditCents;
  });

  if (lignes.length < 2) {
    refus.push({
      etiquette: '[lignes]',
      message: `une écriture compte au moins deux lignes (${lignes.length} ici)`,
    });
  }

  const ecart = debit - credit;
  if (ecart !== 0) {
    refus.push({
      etiquette: '[desequilibre]',
      message: `débit ${debit} centimes, crédit ${credit} centimes : écart de ${ecart}`,
    });
  }

  return {
    validable: refus.length === 0,
    totalDebitCents: debit,
    totalCreditCents: credit,
    ecartCents: ecart,
    refus,
  };
}
